import React, { forwardRef } from 'react'

const RADIUS_OPTIONS = [5, 10, 25, 50, 75, 100, 125, 150, 200, 250]

const STEM_TIME_OPTIONS = [
	{ value: '5', label: '5 min' },
	{ value: '15', label: '15 min' },
	{ value: '30', label: '30 min' },
	{ value: '45', label: '45 min' },
	{ value: '60', label: '1 hour' },
	{ value: '120', label: '2 hours' },
	{ value: '180', label: '3 hours' },
	{ value: '240', label: '4 hours' },
]

const ORIGIN_DATE_STEP_OPTIONS = [
	{ value: '-5', label: '- 5 min' },
	{ value: '-15', label: '- 15 min' },
	{ value: '-30', label: '- 30 min' },
	{ value: '-45', label: '- 45 min' },
	{ value: '-60', label: '- 1 hour' },
	{ value: '-120', label: '- 2 hours' },
	{ value: '-180', label: '- 3 hours' },
	{ value: '-240', label: '- 4 hours' },
]

const DEST_DATE_STEP_OPTIONS = [
	{ value: '5', label: '+ 5 min' },
	{ value: '15', label: '+ 15 min' },
	{ value: '30', label: '+ 30 min' },
	{ value: '45', label: '+ 45 min' },
	{ value: '60', label: '+ 1 hour' },
	{ value: '120', label: '+ 2 hours' },
	{ value: '180', label: '+ 3 hours' },
	{ value: '240', label: '+ 4 hours' },
]

const PostATruckSettings = forwardRef(
	(
		{
			showSettings,
			isDarkMode,
			settings,
			perMileOffsetInput,
			payoutOffsetInput,
			mileBufferInput,
			setPerMileOffsetInput,
			setPayoutOffsetInput,
			setMileBufferInput,
			setSettings,
			handleSaveSettings,
			handleResetSettings,
		},
		ref,
	) => {
		if (!showSettings) return null

		return (
			<div
				ref={ref}
				className={`absolute top-full left-0 mt-2 z-50 w-80 shadow-xl rounded-lg animate-fadeIn ${
					isDarkMode
						? 'bg-gray-800 border border-gray-700'
						: 'bg-white border border-gray-200'
				}`}
				style={{ maxHeight: '32rem', overflowY: 'auto' }}
			>
				<div className="p-4">
					<div className="grid grid-cols-2 gap-3">
						{/* Per Mile Offset */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Per Mile Offset ($/mi)
							</label>
							<input
								type="number"
								step="0.01"
								value={perMileOffsetInput}
								onChange={e => {
									setPerMileOffsetInput(e.target.value)
									const numValue = parseFloat(e.target.value)
									if (!isNaN(numValue)) {
										const newSettings = {
											...settings,
											perMileOffset: numValue,
										}
										setSettings(newSettings)
										handleSaveSettings(newSettings)
									}
								}}
								onBlur={e => {
									const numValue = parseFloat(e.target.value) || 0
									setPerMileOffsetInput(String(numValue))
								}}
								onFocus={e => e.target.select()}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							/>
						</div>

						{/* Payout Offset */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Payout Offset ($)
							</label>
							<input
								type="number"
								step="1"
								value={payoutOffsetInput}
								onChange={e => {
									setPayoutOffsetInput(e.target.value)
									const numValue = parseFloat(e.target.value)
									if (!isNaN(numValue)) {
										const newSettings = {
											...settings,
											payoutOffset: numValue,
										}
										setSettings(newSettings)
										handleSaveSettings(newSettings)
									}
								}}
								onBlur={e => {
									const numValue = parseFloat(e.target.value) || 0
									setPayoutOffsetInput(String(numValue))
								}}
								onFocus={e => e.target.select()}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							/>
						</div>

						{/* Origin Radius */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Origin Radius (mi)
							</label>
							<select
								value={settings.originRadius}
								onChange={e => {
									const newSettings = {
										...settings,
										originRadius: parseInt(e.target.value),
									}
									setSettings(newSettings)
									handleSaveSettings(newSettings)
								}}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							>
								{RADIUS_OPTIONS.map(radius => (
									<option key={radius} value={radius}>
										{radius} mi
									</option>
								))}
							</select>
						</div>

						{/* Destination Radius */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Dest Radius (mi)
							</label>
							<select
								value={settings.destinationRadius}
								onChange={e => {
									const newSettings = {
										...settings,
										destinationRadius: parseInt(e.target.value),
									}
									setSettings(newSettings)
									handleSaveSettings(newSettings)
								}}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							>
								{RADIUS_OPTIONS.map(radius => (
									<option key={radius} value={radius}>
										{radius} mi
									</option>
								))}
							</select>
						</div>

						{/* Mile Buffer */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Mile Buffer (±)
							</label>
							<input
								type="number"
								min="0"
								value={mileBufferInput}
								onChange={e => {
									setMileBufferInput(e.target.value)
									const numValue = parseInt(e.target.value)
									if (!isNaN(numValue) && numValue >= 0) {
										const newSettings = {
											...settings,
											mileBuffer: numValue,
										}
										setSettings(newSettings)
										handleSaveSettings(newSettings)
									}
								}}
								onBlur={e => {
									const numValue = parseInt(e.target.value) || 0
									setMileBufferInput(String(numValue))
								}}
								onFocus={e => e.target.select()}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							/>
						</div>

						{/* Stem Time */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Stem Time
							</label>
							<select
								value={settings.stemTime}
								onChange={e => {
									const newSettings = {
										...settings,
										stemTime: e.target.value,
									}
									setSettings(newSettings)
									handleSaveSettings(newSettings)
								}}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							>
								{STEM_TIME_OPTIONS.map(option => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>

						{/* Origin Date Step */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Origin Time Offset
							</label>
							<select
								value={settings.originDateStep}
								onChange={e => {
									const newSettings = {
										...settings,
										originDateStep: e.target.value,
									}
									setSettings(newSettings)
									handleSaveSettings(newSettings)
								}}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							>
								{ORIGIN_DATE_STEP_OPTIONS.map(option => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>

						{/* Destination Date Step */}
						<div>
							<label
								className={`block text-xs font-medium mb-1 ${
									isDarkMode ? 'text-gray-300' : 'text-gray-700'
								}`}
							>
								Dest Time Offset
							</label>
							<select
								value={settings.destinationDateStep}
								onChange={e => {
									const newSettings = {
										...settings,
										destinationDateStep: e.target.value,
									}
									setSettings(newSettings)
									handleSaveSettings(newSettings)
								}}
								className={`w-full px-2 py-1 text-xs border rounded ${
									isDarkMode
										? 'bg-gray-700 border-gray-600 text-white'
										: 'bg-white border-gray-300'
								}`}
							>
								{DEST_DATE_STEP_OPTIONS.map(option => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</div>
					</div>

					{/* Settings Actions */}
					<div className="mt-3">
						<button
							onClick={handleResetSettings}
							className={`w-full px-3 py-1 text-xs rounded ${
								isDarkMode
									? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
									: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
							}`}
						>
							Reset to Defaults
						</button>
					</div>
				</div>
			</div>
		)
	},
)

PostATruckSettings.displayName = 'PostATruckSettings'

export default PostATruckSettings
